import React from 'react';
import {ListOrdered} from 'lucide-react';
import type {WorkSession} from '../models/session.model';

interface SessionsHistoryTableProps {
  sessions: WorkSession[];
  loading?: boolean;
  onRowClick?: (session: WorkSession) => void;
  onOpenDetails?: (session: WorkSession) => void;
  selectedDate?: string | null;
}

function formatDistance (meters: number | null): string {
  if (meters === null || meters === undefined) {return '—';}
  return `${(meters / 1000).toFixed(2)} كم`;
}

function formatDuration (secs: number | null): string {
  if (secs === null || secs === undefined) {return '—';}
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}س ${m}د`;
}

export const SessionsHistoryTable: React.FC<SessionsHistoryTableProps> = ({
  sessions,
  loading = false,
  onRowClick,
  onOpenDetails,
  selectedDate
}) => {
  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-center text-gray-500">
        جارٍ التحميل...
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-gray-500">
        لا يوجد سجل جلسات لهذا المسوّق
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 font-bold">
        سجل الجلسات
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-right">
          <thead className="bg-gray-50 text-sm text-gray-600">
            <tr>
              <th className="px-4 py-2 font-medium">التاريخ</th>
              <th className="px-4 py-2 font-medium">المسافة</th>
              <th className="px-4 py-2 font-medium">الزيارات</th>
              <th className="px-4 py-2 font-medium">المدة</th>
              <th className="px-4 py-2 font-medium">الحالة</th>
              {onOpenDetails && (
                <th className="px-4 py-2 font-medium">الإجراءات</th>
              )}
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => {
              const isSelected = selectedDate === s.workDate;
              return (
                <tr
                  key={s.id}
                  className={`cursor-pointer border-t border-gray-100 text-sm transition-colors ${
                    isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => onRowClick?.(s)}
                >
                  <td className="px-4 py-3 font-medium">{s.workDate}</td>
                  <td className="px-4 py-3">{formatDistance(s.totalDistanceMeters)}</td>
                  <td className="px-4 py-3">{s.totalStoresVisited}</td>
                  <td className="px-4 py-3">{formatDuration(s.durationSeconds)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        s.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {s.status === 'active' ? 'نشطة' : 'منتهية'}
                    </span>
                  </td>
                  {onOpenDetails && (
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenDetails(s);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                        title="عرض الجدول الزمني"
                      >
                        <ListOrdered className="h-3.5 w-3.5" />
                        <span>الجدول الزمني</span>
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
